import { Component, OnInit, Inject } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToolDeliverableType, ToolDeliverable, Attachment, User, GeneralSettings, ToolDeliverableStatus } from 'app/profile/models/generic.model';
import { I18nService } from 'app/shared/services/i18n.service';
import { ProfileService } from 'app/profile/services/profile.service';
import { AuthData } from '@tesla/ng-auth';
import { TslDialog, TslOption } from '@tesla/ng-tds';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserDetails } from 'app/profile/models/user.model';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import * as FileSaver from 'file-saver';
import { Store, select } from '@ngrx/store';
import { GetSupplierProfileCurrentUser } from 'app/profile/store/app.actions';
import { getGeneralSettings, SelectCurrentUser } from 'app/profile/store/app.selector';
import { GeneralSettingsState } from 'app/profile/store/state/general.state';


@Component({
    selector: 'app-deliverable-form-dialog',
    templateUrl: './deliverable-form-dialog.component.html',
    styleUrls: ['./deliverable-form-dialog.component.scss']
})
export class DeliverableFormDialogComponent implements OnInit {
    deleteTitle: string;
    deleteConfirmation: string;
    deliverableTypes: ToolDeliverableType[] = [];
    deliverableForm: FormGroup;
    userDetails: UserDetails;
    user: User;
    displayName = "";
    settings: GeneralSettings;
    showCMTChanges: any;


    get eventType() { return this.deliverableForm.get('eventType'); }
    get deliverableType() { return this.deliverableForm.get('deliverableType'); }
    get start() { return this.deliverableForm.get('start'); }
    get planned() { return this.deliverableForm.get('planned'); }
    get actual() { return this.deliverableForm.get('actual'); }
    get isEditMode(): boolean { return this.data.eventType > -1; }
    get isDefault(): boolean { return this.data.model.isDefault; }

    get isNotSubmitted(): boolean {
        return (this.data.model.status === ToolDeliverableStatus.NotSubmitted || this.data.model.status === undefined) || this.data.model.status === ToolDeliverableStatus.Rejected;
    }
    get hasApprovalComment(): boolean {
        return (this.data.model.approvalComment !== "" && this.data.model.approvalComment !== null) && this.data.model.approvalComment !== undefined;
    }
    get hasNoFileAttachment(): boolean {
        return !this.data.model.attachments || this.data.model.attachments.length === 0;
    }

    constructor(private fb: FormBuilder,
        private authData: AuthData,
        private translate: I18nService,
        private supplierStore: Store<SupplierState>,
        private store: Store<GeneralSettingsState>,
        private service: ProfileService,
        private dialog: TslDialog,
        public dialogRef: MatDialogRef<DeliverableFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data:
            {
                eventType: number,
                subCategoryId: number,
                supplierCode: string,
                model: ToolDeliverable
            }) {
        this.deliverableForm = this.fb.group({
            eventType: ['', Validators.required],
            deliverableType: ['', [Validators.required, Validators.pattern("^([0-9a-zA-Z ])+$")]],
            start: ['', Validators.required],
            planned: ['', Validators.required],
            actual: ['', Validators.required],
            approvalComment: ['', Validators.required],
            supplierComment: ['', Validators.required],
        });

        this.deliverableForm.valueChanges
            .subscribe(checkValue => {
                const appComment = this.deliverableForm.get('approvalComment');
                const supComment = this.deliverableForm.get('supplierComment');
                const delType = this.deliverableForm.get('deliverableType');
                if (!this.userDetails.isSIE || this.isNotSubmitted) {
                    appComment.clearValidators();
                    supComment.setValidators(Validators.required);
                } else {
                    supComment.clearValidators();
                    appComment.setValidators(Validators.required);
                }
                if (this.data.model.isDefault) {
                    delType.clearValidators();
                }
                appComment.updateValueAndValidity();
                supComment.updateValueAndValidity();
                delType.updateValueAndValidity();
            });
    }

    ngOnInit() {
        const userInfo = this.authData.getUserInfo();
        this.displayName = userInfo?.display_name;
        // const isTeslaUser = userInfo?.is_employee;
        this.supplierStore.dispatch(new GetSupplierProfileCurrentUser());

        this.supplierStore.pipe(select(SelectCurrentUser)).subscribe(result => {
            if (result) {
                this.store.select(getGeneralSettings).subscribe(res => this.settings = res);
                this.showCMTChanges = this.settings.settings.showCMTChanges;
                this.userDetails = result;
            }
        });

        this.loadResources();

        this.deliverableForm.patchValue({
            eventType: this.data.eventType,
            deliverableType: this.data.model.deliverable,
            start: this.data.model.startDate,
            planned: this.data.model.plannedDate,
            actual: this.data.model.actualDate,
            approvalComment: this.data.model.approvalComment,
            supplierComment: this.data.model.supplierComment
        });
    }

    get isSIE(): boolean { return this.userDetails.isSIE; }

    onClose(): void {
        this.dialogRef.close();
    }

    onSaveWithoutSubmit(): void {
        if(this.start.value > this.planned.value || this.start.value > this.actual.value) {
            this.dialog.alert('Incorrect Dates', "Start date can be either before or equal to the Planned Date and/or Actual date.");
            return;
        }
        if(this.planned.value > this.actual.value){
            this.dialog.alert('Incorrect Dates', "Planned date can be either before or equal to the Actual date.");
            return;
        }
        const selectedType = this.deliverableTypes.find(d => d.deliverable === this.deliverableForm.value.deliverableType);
        this.data.model.isCreatedByTeslaUser = this.userDetails.isTeslaUser;
        this.data.model.eventType = this.eventType.value;
        this.data.model.deliverableTypeId = selectedType !== undefined ? selectedType.id : 0;
        this.data.model.deliverable = this.deliverableForm.value.deliverableType;
        this.data.model.startDate = this.deliverableForm.value.start;
        this.data.model.plannedDate = this.deliverableForm.value.planned;
        this.data.model.actualDate = this.deliverableForm.value.actual;
        this.data.model.supplierComment = this.deliverableForm.value.supplierComment;
        this.data.model.approvalComment = this.deliverableForm.value.approvalComment;
        this.dialogRef.close({ eventType: this.eventType.value, model: this.data.model });
        this.data.model.status = ToolDeliverableStatus.NotSubmitted;
    }

    onApproved(): void {
        this.savedeliverableData();
        this.data.model.approver = this.displayName;
        this.data.model.approvalDate = new Date();
        this.data.model.approvalComment = this.deliverableForm.value.approvalComment;

        this.data.model.status = ToolDeliverableStatus.Approved;
        this.dialogRef.close({ eventType: this.eventType.value, model: this.data.model });
    }

    onReject(): void {
        this.data.model.approver = this.displayName;
        this.data.model.approvalDate = new Date();
        this.data.model.approvalComment = this.deliverableForm.value.approvalComment;
        this.data.model.status = ToolDeliverableStatus.Rejected;
        this.dialogRef.close({ eventType: this.eventType.value, model: this.data.model });
    }

    onSave(): void {
        if(this.start.value > this.planned.value || this.start.value > this.actual.value) {
            this.dialog.alert('Incorrect Dates', "Start date can be either before or equal to the Planned Date and/or Actual date.");
            return;
        }
        if(this.planned.value > this.actual.value){
            this.dialog.alert('Incorrect Dates', "Planned date can be either before or equal to the Actual date.");
            return;
        }
        this.dialog.confirm('Confirm Inputs', "Start date and Planned date cannot be updated once submitted, are you sure you would like to proceed?")
            .afterClosed()
            .subscribe(confirmed => {
                if(confirmed) {
                    this.savedeliverableData();
                    this.data.model.supplierComment = this.deliverableForm.value.supplierComment;
                    if (this.data.model.supplierComment === undefined) {
                        this.data.model.supplierComment = this.deliverableForm.value.approvalComment;
                    } else {
                        this.data.model.approvalComment = this.deliverableForm.value.approvalComment;
                    }
                    this.dialogRef.close({ eventType: this.eventType.value, model: this.data.model });
                    this.data.model.submitter = this.displayName;
                    this.data.model.status = ToolDeliverableStatus.Submitted;
                    this.data.model.submitDate = new Date();
                }
            });
    }

    savedeliverableData(): void {
        if (this.deliverableForm.invalid) {
            return;
        }
        const selectedType = this.deliverableTypes.find(d => d.deliverable === this.deliverableForm.value.deliverableType);
        this.data.model.eventType = this.eventType.value;
        this.data.model.isCreatedByTeslaUser = this.userDetails.isTeslaUser;
        this.data.model.deliverableTypeId = selectedType !== undefined ? selectedType.id : 0;
        this.data.model.deliverable = this.deliverableForm.value.deliverableType;
        this.data.model.startDate = this.deliverableForm.value.start;
        this.data.model.plannedDate = this.deliverableForm.value.planned;
        this.data.model.actualDate = this.deliverableForm.value.actual;
    }

    onSelectType(option: TslOption): void {
        const type: ToolDeliverableType = option.value;
        this.deliverableType.setValue(type.deliverable);
    }

    onDeleteAttachment(attach: Attachment): void {
        const index = this.data.model.attachments.findIndex(a => a.key === attach.key);
        if (index !== -1) {
            this.dialog.confirm(this.translate.get(this.deleteTitle), this.translate.get(this.deleteConfirmation))
                .afterClosed()
                .subscribe(confirmed => {
                    if (confirmed) {
                        this.data.model.attachments.splice(index, 1);
                    }
                });
        }
    }

    onDownloadAttachment(attach: Attachment): void {
        this.service.downloadCertificationAttachment(this.data.supplierCode, attach.key)
            .subscribe(file => {
                FileSaver.saveAs(file, attach.name);
            });
    }

    onUploadFile(files: File[]): void {
        for (let file = 0; file < files.length; file++) {
            if (files[file].size > 10240000) {
                var name = files[file].name;
                this.dialog.alert(this.translate.get('Alert'), this.translate.get(name + ' size can not be more than 10MB!'));
                return;
            }

            this.service.postToolAttachment(this.data.supplierCode, files[file])
                .subscribe(res => {
                    if (!this.data.model.attachments) {
                        this.data.model.attachments = [];
                    }

                    this.data.model.attachments.push(res);
                });
        }
    }

    getFileType(filename: string): string {
        const nameArray = filename.split(".");
        return nameArray[nameArray.length - 1];
    }

    private loadResources(): void {
        this.service.getDeliverableType(this.data.supplierCode, this.data.subCategoryId)
            .subscribe(res => this.deliverableTypes = res);

        this.translate.getAsync('Delete Item').subscribe(t => this.deleteTitle = t);
        this.translate.getAsync('Are you sure you want to delete this item?').subscribe(t => this.deleteConfirmation = t);
    }
}
